<template>
  <div class="app-text-field">
    <div v-if="label" class="field-label">
      <v-icon v-if="labelIcon" size="18">{{ labelIcon }}</v-icon>
      <span>{{ label }}</span>
    </div>
    <v-autocomplete
      v-model="addressModel"
      v-model:search="search"
      v-bind="fieldAttrs"
      :class="fieldClass"
      :items="items"
      :loading="loading"
      :hide-no-data="!search"
      :menu-props="{ maxHeight: 320, contentClass: 'address-autocomplete-menu', eager: true, transition: false }"
      :no-filter="true"
      :placeholder="resolvedPlaceholder"
      persistent-placeholder
      :hide-details="hideDetails"
      :variant="variant"
      :flat="flat"
      :menu-icon="items.length ? 'mdi-chevron-down' : ''"
      :prepend-inner-icon="prependInnerIcon || undefined"
      autocomplete="off"
      item-title="title"
      item-value="value"
      @update:modelValue="handleSelect"
    >
      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          class="address-autocomplete-item"
          density="compact"
          :title="item.title"
          :subtitle="item.raw?.subtitle"
        >
          <template #prepend>
            <v-icon size="18" color="primary">mdi-map-marker-outline</v-icon>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useAttrs, watch } from "vue";
import { searchAddress } from "@/shared/services/http.geo-config";

defineOptions({
  inheritAttrs: false,
});

type AddressSuggestion = {
  title: string;
  subtitle?: string;
  value: string;
  raw: Record<string, any>;
};

const model = defineModel<string | null>({ default: null });
const emit = defineEmits<{
  (e: "selected", value: Record<string, any> | null): void;
}>();
const search = ref("");
const items = ref<AddressSuggestion[]>([]);
const loading = ref(false);
const attrs = useAttrs();
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let suppressNextSearch = false;
let isApplyingSelection = false;

const props = withDefaults(
  defineProps<{
    defaultValue?: string | null;
    variant?: "solo" | "filled" | "outlined" | "plain" | "solo-filled" | "solo-inverted" | "underlined";
    flat?: boolean;
    placeholder?: string;
    hideDetails?: boolean | "auto";
    label?: string;
    labelIcon?: string;
    prependInnerIcon?: string;
  }>(),
  {
    defaultValue: null,
    variant: "solo",
    flat: true,
    placeholder: "Enter address",
    hideDetails: true,
    label: "",
    labelIcon: "",
    prependInnerIcon: "mdi-map-marker-outline",
  },
);

const fieldClass = computed(() => ["app-text-field__input", attrs.class]);
const addressModel = computed({
  get: () => (model.value === "" ? null : model.value),
  set: (value: string | null) => {
    model.value = value ?? "";
  },
});
const resolvedPlaceholder = computed(() => props.placeholder?.trim() || "Enter address");
const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});

watch(
  () => props.defaultValue,
  (next) => {
    const value = next?.trim?.() ?? "";
    if (!value) return;
    if (model.value == null || model.value === "") {
      model.value = value;
    }
  },
  { immediate: true },
);

function formatSuggestion(item: Record<string, any>) {
  return String(item?.full_address || "").trim();
}

function toSuggestion(item: Record<string, any>): AddressSuggestion {
  const title = formatSuggestion(item);
  const subtitle = String([item?.city, item?.state, item?.zipcode, item?.country].filter(Boolean).join(", ")).trim();

  return {
    title,
    subtitle,
    value: title,
    raw: item,
  };
}

function normalizeResponse(data: any): Record<string, any>[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function handleSelect(next: string | null) {
  const selectedItem = items.value.find((item) => item.value === next) ?? null;
  const payload = selectedItem?.raw ?? null;
  isApplyingSelection = true;
  suppressNextSearch = true;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = null;
  emit("selected", payload);
  queueMicrotask(() => {
    isApplyingSelection = false;
  });
}

async function runSearch(query: string) {
  const value = query.trim();
  if (!value) {
    items.value = [];
    return;
  }

  loading.value = true;
  try {
    const data = await searchAddress({
      full_address: value,
      limit: 10,
    });
    items.value = normalizeResponse(data).map(toSuggestion);
  } catch (err) {
    const message = String((err as any)?.message ?? err ?? "");
    const isTimeout = message.includes("timeout") || message.includes("exceeded");

    if (isTimeout) {
      console.warn("AppAddressField search timed out");
    } else {
      console.error("AppAddressField search error:", err);
    }
    items.value = [];
  } finally {
    loading.value = false;
  }
}

watch(search, (next) => {
  if (isApplyingSelection) return;

  if (suppressNextSearch) {
    suppressNextSearch = false;
    return;
  }

  if (next && next.trim() === (model.value || "").trim()) {
    return;
  }

  if (searchTimer) clearTimeout(searchTimer);

  searchTimer = setTimeout(() => {
    void runSearch(next || "");
  }, 300);
});

watch(model, (next) => {
  suppressNextSearch = true;
  search.value = next ?? "";
});

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
});
</script>

<style scoped>
.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-subtitle);
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  padding: 0 10px;
}

.app-text-field {
  position: relative;
}

.app-text-field__input {
  --v-input-control-height: 48px;
  --v-input-padding-top: 0px;
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.app-text-field__input :deep(.v-field) {
  border: 1px solid #eaeaea;
}

.app-text-field__input :deep(.v-field__input) {
  font-size: 0.875rem;
}

.app-text-field__input :deep(.v-input--density-default .v-field--variant-outlined),
.app-text-field__input :deep(.v-input--density-default .v-field--single-line),
.app-text-field__input :deep(.v-input--density-default .v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo-inverted),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo-filled),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-filled) {
  --v-input-control-height: 48px;
  --v-field-padding-bottom: 0px !important;
}

:deep(.address-autocomplete-menu) {
  box-shadow: none !important;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.address-autocomplete-item {
  overflow: hidden;
  min-width: 0;
  box-shadow: none !important;
  min-height: 42px;
  padding-block: 4px;
}

.address-autocomplete-item :deep(.v-list-item__prepend) {
  margin-inline-end: 4px;
}

.address-autocomplete-item :deep(.v-list-item__prepend .v-list-item__spacer) {
  width: 2px;
}

.address-autocomplete-item :deep(.v-list-item__content) {
  min-width: 0;
  overflow: hidden;
}

.address-autocomplete-item :deep(.v-list-item-title),
.address-autocomplete-item :deep(.v-list-item-subtitle) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-autocomplete-item :deep(.v-list-item-title) {
  font-size: 0.82rem;
  /* line-height: 1.2; */
}

.address-autocomplete-item :deep(.v-list-item-subtitle) {
  font-size: 0.82rem;
  /* line-height: 1.2; */
}

/* Force error message color to red to match API validation styling */
.app-text-field__input :deep(.v-messages__message) {
  color: #dc2626 !important;
  font-size: 13px;
}
</style>
